import { AfterViewInit, Component, OnDestroy, ViewEncapsulation, effect, input, output, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { finalize, map, takeUntil, tap } from "rxjs/operators";
import { AuthorizationService, ConfirmationDialogInputService, ConfirmationDialogService, NotificationPopupService } from '@myapp/modules/fwcore/services';
import { ListViewComponent, ListViewModule } from "@progress/kendo-angular-listview";
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { AdminService } from '../../../services';
import { NgClass, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { CheckBoxModule } from "@progress/kendo-angular-inputs";
import { RequestDelete, RequestUserPermission } from "../../../../../modules/fwcore/models";
import { RequestRole, RequestRoleSet, Role, RoleChecked, RoleSave } from "../../../models";
import { RolesEditorComponent } from "../roles-editor/roles-editor.component";
import { IsgSizerDirective } from "../../../../prob/directives/isg-sizer.directive";

@Component({
  selector: 'app-roles-list',
  templateUrl: './roles-list.component.html',
  styleUrls: ['./roles-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [NgIf, ButtonModule, IsgSizerDirective, ListViewModule, NgClass, FormsModule, CheckBoxModule, RolesEditorComponent]
})
export class RolesListComponent implements AfterViewInit, OnDestroy {

  //----------------------------  Constants  ---------------------------

  /** Notification message when a role is added. */
  protected readonly ctMsgRoleAdded = 'Role added succesfully';

  /** Notification message when a role is updated. */
  protected readonly ctMsgRoleUpdated = 'Role updated succesfully';

  //--------------------  Input / Output Properties  -------------------

  /** Module id used to filter the roles fetch. */
  public moduleId = input<string | undefined | null>(null);

  /** License id context for the list. */
  public licenseId = input<string | null>(null);

  /** User id context for the list. */
  public userId = input<string | null>(null);

  /** Shows the checkbox column when true. */
  public showCheckbox = input(false);

  /** Shows the toolbar when true. */
  public showToolbar = input(false);

  /** Shows the save button when true. */
  public showSaveButton = input(false);

  /** Shows the read-only button when true. */
  public showReadOnlyButton = input(false);

  /** Shows the delete button when true. */
  public showDelButton = input(false);

  /** Shows the create-template button when true. */
  public showTemplateButton = input(false);

  /** Retained for legacy callers; sizing is now pane-driven */
  public minHeight = input(200);

  /** Retained for legacy callers; sizing is now pane-driven */
  public widthThreshold = input(600);

  /** Total height the component may occupy */
  public heightControl = input(300);

  /** Ids of items to mark as checked; applied to the loaded items. */
  public checkedIds = input<string[]>([]);

  /** Emits the selected role, or null when selection clears. */
  public selected = output<Role | null>();

  /** Emits when the save-role-permissions button is clicked. */
  public saveRolePermissionsClicked = output<boolean>();

  //----------------------  ViewChild Properties  ----------------------

  /** Reference to the kendo listview. */
  protected listview = viewChild.required(ListViewComponent);

  //------------------------  Public Properties  -----------------------

  /** Currently selected role. */
  public selectedItem?: Role | null;

  /** Calculated height of the list. */
  public heightList: number = 200;

  //------------------------  State Properties  ------------------------

  /** Module id applied to the last roles fetch */
  private currentModuleId: string | undefined | null = undefined;

  /** Emits on component destruction to complete open subscriptions. */
  protected destroy$ = new Subject();

  /** Role loaded into the editor dialog. */
  protected editDataItem: Role | null | undefined;

  /** True when the editor is opened in view-only mode. */
  protected isViewOnlyEditor: boolean = true;

  /** True while data is loading. */
  protected loading: boolean = true;

  /** Controls visibility of the editor. */
  protected showEditor: boolean = false;

  //-------------------  Data Collection Properties  -------------------

  /** Roles displayed in the listview. */
  protected dataItems: RoleChecked[] = [];

  //----------------------  Permission Properties  ---------------------

  /** Permission request payload for the current route. */
  protected reqPerm: RequestUserPermission = {
  }

  //---------------------------  Constructor  --------------------------

  constructor(protected router: Router
    , protected serviceAuth: AuthorizationService
    , protected adminService: AdminService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogConfirmInput: ConfirmationDialogInputService
    , protected notification: NotificationPopupService
  ) {

    this.setupEffectHandlers();
  }

  //-------------------------  Public Methods  -------------------------

  /** @deprecated Sizing is measured internally; retained so legacy callers compile. */
  public heightChange(_options?: ControlResizerOptions): void {
  }

  /** Clears the current selection. */
  public clearSelection() {

    this.selectedItem = null;
    this.listview().focus(undefined);
  }

  /** Returns the ids of the currently checked items. */
  public getCheckedIds(): string[] {

    const checkedItemIds: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.id!);

    return checkedItemIds ?? [];
  }

  /** Applies the supplied ids as the checked items. */
  public setCheckedIds(ids: string[]): void {

    this.applyCheckedIds(ids);
  }

  /** Returns the names of the currently checked items. */
  public getCheckedNames(): string[] {

    const names: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.name!);

    return names;
  }

  /** Returns the currently checked role items. */
  public getCheckedDataItems(): Role[] {

    const items: Role[] = this.dataItems
      .filter((item) => item.checked);

    return items;
  }

  //------------------------  Lifecycle Methods  -----------------------

  /**
   * Captures the current route for permission requests
   */
  ngAfterViewInit(): void {

    this.reqPerm.route = this.router.url;
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Reacts to input changes; replaces the former ngOnChanges wiring. */
  protected setupEffectHandlers(): void {

    effect(() => {

      const moduleId = this.moduleId();

      if (moduleId === this.currentModuleId) {
        return;
      }

      this.currentModuleId = moduleId;

      if (!moduleId) {
        return;
      }

      this.getRoles();
    });

    // Applies parent-supplied checked ids to the currently loaded items;
    // replaces the former checkedIds input setter.
    effect(() => {

      this.applyCheckedIds(this.checkedIds());
    });
  }

  //-------------------------  Event Handlers  -------------------------

  /** Opens the editor in add mode. */
  protected onAddClick(): void {

    this.isViewOnlyEditor = false;
    this.setShowEditor(true);
  }

  /** Opens the editor in edit mode for the selected role. */
  protected onEditClick() {

    this.isViewOnlyEditor = false;
    this.setShowEditor(true);
    this.editDataItem = this.selectedItem;
  }

  /** Opens the editor in view-only mode for the selected role. */
  protected onViewClick() {

    this.isViewOnlyEditor = true;
    this.setShowEditor(true);
    this.editDataItem = this.selectedItem;
  }

  /** Prompts for a name and copies the selected role. */
  protected onCopyClick() {

    const txt = "Please specify a new name for the duplicate role '"
      + this.selectedItem!.name
      + "'. You can add/remove permissions and will need to assign users afterwards."
      ;

    this.dialogConfirmInput.open("Copy Role"
      , txt).
      subscribe((result: any) => {

        if (result.success) {
          this.copyRole(this.selectedItem!, result.value);
        }
      });
  }

  /** Prompts for a name and creates a template from the selected role. */
  protected onTemplateClick() {

    const txt = "Please specify a name for the template. This will be created with the same permissions as the role '"
      + this.selectedItem!.name
      + "' and will be available for all companies to select when creating new roles.";

    this.dialogConfirmInput.open("Confirm Template Creation"
      , txt).
      subscribe((result: any) => {

        if (result.success) {
          this.createTemplateFromRole(this.selectedItem!, result.value);
        }
      });
  }

  /** Signals the parent to save the role permissions. */
  protected onSaveRolePermissionsClick() {

    this.saveRolePermissionsClicked.emit(true);
  }

  /** Confirms and deletes the selected role. */
  protected onDeleteClick(): void {

    this.dialogConfirm.open('Confirm Deletion', 'Are you sure you want to delete this role?')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {

        if (result.value
          && this.selectedItem?.id) {
          this.deleteRole(this.selectedItem.id);
        }
      });
  }

  /** Closes the editor without saving. */
  protected onCancelEditor(): void {

    this.setShowEditor(false);
    this.editDataItem = null;
  }

  /** Persists the editor changes and closes the editor. */
  protected onSaveEditor(item: RoleSave): void {

    item.licenseId = this.licenseId();

    this.setRole(item);
    this.setShowEditor(false);
  }

  /** Handles item clicks, toggling selection and emitting the result. */
  protected onClickItemEvent(index: number, dataItem: Role, event: Event): void {

    let newSelection: Role = dataItem;

    if (this.selectedItem == newSelection) {

      this.clearSelection();
      this.selected.emit(this.selectedItem);

    }
    else {

      this.selectedItem = newSelection;
      this.listview().focus(index);
      this.selected.emit(this.selectedItem);

    }

    event.stopPropagation();
  }

  /** Placeholder for listview click events. */
  protected onClickListView(event: any) {
  }

  /** Placeholder for listview change events. */
  protected onChangeListView(event: any): void {
  }

  //--------------------------  List Methods  --------------------------

  /** Applies the supplied ids as the checked items. */
  protected applyCheckedIds(ids: string[]): void {

    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }

  /** Sets the editor visibility. */
  protected setShowEditor(condition: boolean): void {

    this.showEditor = condition;
  }

  /** Replaces the local role matching the updated role's id. */
  protected updateLocalRoleById(updatedRole: Role): void {

    const index = this.dataItems.findIndex(item => item.id === updatedRole.id);

    if (index !== -1) {
      this.dataItems[index] = { ...this.dataItems[index], ...updatedRole };
    }
  }

  /** Removes the local role matching the id. */
  protected removeLocalRoleById(id: string): void {

    const index = this.dataItems.findIndex(item => item.id === id);

    if (index !== -1) {
      this.dataItems.splice(index, 1);
    }
  }

  //---------------------------  Api Methods  --------------------------

  /** Fetches the roles for the current filters. */
  protected getRoles(): void {

    this.loading = true;

    const req: RequestRole = {
      licenseId: this.licenseId()
      , moduleId: this.moduleId()
    };

    this.adminService.getRole$(req)
      .pipe(takeUntil(this.destroy$)
        , tap(() => { this.loading = false; })
        , map((resp) => {

          const updatedItems: RoleChecked[] = resp.data?.map((item: Role) => ({
            ...item,
            checked: false,
          })) ?? [];

          return updatedItems;
        }))
      .subscribe(
        (items) => {
          this.dataItems = items;
        }
      );
  }

  /** Adds or updates a role and applies the result locally. */
  protected setRole(item: RoleSave): void {

    const req: RequestRoleSet = {
      data: [item]
      , moduleId: this.moduleId()
    };

    this.adminService.AddUpdate$(req).pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {

        if (res.success
          && res.data
          && res.data?.length > 0) {

          const nRole = res.data[0];
          const existingRole = this.dataItems.find(item => item.id === nRole.id);

          if (!existingRole) {
            this.dataItems.push(nRole);
            this.notification.showNotification(this.ctMsgRoleAdded, 'success');
          }
          else {
            this.updateLocalRoleById(nRole);
            this.notification.showNotification(this.ctMsgRoleUpdated, 'success');
            this.editDataItem = null;
          }

          // Angular does not track changes to the collection elements
          // In order to fix the issue, we make a copy and reassign
          this.dataItems = [...this.dataItems];
        }
      });
  }

  /** Copies a role under a new name and applies the result locally. */
  protected copyRole(item: RoleSave, name: string) {

    let citem: RoleSave = {
      copyRoleId: item.id
      , licenseId: item.licenseId
      , name: name
      , allLicenseUsers: false
      , roleType: item.roleType
    };

    const req: RequestRoleSet = {
      data: [citem]
      , moduleId: this.moduleId()
    };

    this.adminService.AddUpdate$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {

        if (res.success
          && res.data
          && res.data?.length > 0) {

          const nRole = res.data[0];
          const existingRole = this.dataItems.find(item => item.id === nRole.id);

          this.dataItems = [...this.dataItems];

          if (!existingRole) {
            this.dataItems.push(nRole);
            this.notification.showNotification(this.ctMsgRoleAdded, 'success');
          }
          else {
            this.updateLocalRoleById(nRole);
            this.notification.showNotification(this.ctMsgRoleUpdated, 'success');
            this.editDataItem = null;
          }
        }
      });
  }

  /** Creates a template from a role and applies the result locally. */
  protected createTemplateFromRole(item: RoleSave, name: string) {

    let tItem: RoleSave = {
      roleIdAsTemplate: item.id
      , name: name
      , roleType: item.roleType
    };

    const req: RequestRoleSet = {
      data: [tItem]
      , moduleId: this.moduleId()
    };

    this.adminService.AddUpdate$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {

        if (res.success
          && res.data
          && res.data?.length > 0) {

          const nRole = res.data[0];
          const existingRole = this.dataItems.find(item => item.id === nRole.id);

          this.dataItems = [...this.dataItems];

          if (!existingRole) {
            this.dataItems.push(nRole);
            this.notification.showNotification(this.ctMsgRoleAdded, 'success');
          }
          else {
            this.updateLocalRoleById(nRole);
            this.notification.showNotification(this.ctMsgRoleUpdated, 'success');
            this.editDataItem = null;
          }
        }
      });
  }

  /** Deletes a role and removes it locally on success. */
  protected deleteRole(roleId: string): void {

    const req: RequestDelete = {
      id: roleId
      , moduleId: this.moduleId()
    };

    this.adminService.removeRole$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => this.loading = false))
      .subscribe((res) => {

        if (res.success) {
          this.removeLocalRoleById(roleId);
          this.clearSelection();
          this.dataItems = [...this.dataItems];
          this.notification.showNotification('Role deleted succesfully', 'success');
        }
        else {
          this.notification.showNotification(res.message ?? "Failed to delete role", 'error');
        }
      });
  }

}
